/**
 * CreateCreditNote.tsx
 *
 * Professional credit note creation screen matching your invoice UI style.
 * - Customer autocomplete (reuses your getUsers)
 * - Invoice lookup (reuses your getOutstanding)
 * - Line item table with HSN, Qty, Rate, Discount%, Tax%, Amount
 * - Summary panel (subtotal, tax, discount, grand total)
 * - Reason dropdown + notes
 * - PDF generation on submit
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";

import {
  Alert, Autocomplete, Box, Button, Card, CardContent,
  Chip, CircularProgress, Container, Divider, FormControl,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";

import AddIcon          from "@mui/icons-material/Add";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import DeleteIcon       from "@mui/icons-material/Delete";
import NoteAltIcon      from "@mui/icons-material/NoteAlt";
import SaveIcon         from "@mui/icons-material/Save";

import { getUsers }      from "../Services/userService";
import { getOutstanding }from "../Services/InvoiceReceiptService";
import { createCreditNote } from "../Services/CreditDebitNoteService";
import { getOrganizationDetails } from "../Services/orgService";
import {
  INoteItem,
  CreditNoteReason,
} from "../Interfaces/ICreditDebitNote";

// ─── Constants ────────────────────────────────────────────────────────────────

const CREDIT_REASONS: { value: CreditNoteReason; label: string }[] = [
  { value: "RETURN",       label: "Goods Return" },
  { value: "DISCOUNT",     label: "Discount / Allowance" },
  { value: "OVERBILLING",  label: "Overbilling Correction" },
  { value: "DAMAGED_GOODS",label: "Damaged Goods" },
  { value: "OTHER",        label: "Other" },
];

const TAX_RATES = [0, 3, 5, 12, 18, 28];

const emptyItem = (): INoteItem => ({
  itemId:      "",
  description: "",
  hsn:         "",
  quantity:    1,
  rate:        0,
  discount:    0,
  tax:         0,
  amount:      0,
});

const computeAmount = (item: INoteItem): number =>
  item.quantity * item.rate * (1 - item.discount / 100) * (1 + item.tax / 100);

const fmt = (n: number) =>
  `Rs.  ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// ─── PDF ─────────────────────────────────────────────────────────────────────

function printCreditNotePDF(cnId: string, data: any, orgDetails: any) {
  const doc = new jsPDF();
  const W   = doc.internal.pageSize.width;
  const H   = doc.internal.pageSize.height;

  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("CREDIT NOTE", W / 2, 12, { align: "center" });

  // Org box
  doc.rect(14, 18, 88, 44);
  doc.setFontSize(11); doc.text(orgDetails?.OrgName || "N/A", 16, 26);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const addr = `${orgDetails?.address?.[0]?.addressLine1 || ""}, ${orgDetails?.address?.[0]?.city || ""}, ${orgDetails?.address?.[0]?.state || ""}`;
  doc.text(doc.splitTextToSize(addr, 84), 16, 32);
  doc.text(`GSTIN: ${orgDetails?.GST || ""}`, 16, 42);
  doc.text(`Email: ${orgDetails?.email || ""}`, 16, 47);
  doc.text(`Phone: ${orgDetails?.phone || ""}`, 16, 52);

  // CN details box
  doc.rect(108, 18, 88, 44);
  doc.setFont("helvetica", "bold");
  doc.text("Credit Note No.:", 110, 26); doc.setFont("helvetica", "normal");
  doc.text(cnId, 110, 32);
  doc.setFont("helvetica", "bold"); doc.text("Date:", 110, 40); doc.setFont("helvetica", "normal");
  doc.text(new Date(data.cnDate).toLocaleDateString("en-GB"), 110, 46);
  doc.setFont("helvetica", "bold"); doc.text("Against Invoice:", 110, 54); doc.setFont("helvetica", "normal");
  doc.text(data.referenceId, 110, 60);

  // Customer box
  let y = 72;
  doc.rect(14, y, 182, 30);
  doc.setFont("helvetica", "normal"); doc.text("Customer:", 16, y + 7);
  doc.setFontSize(11); doc.setFont("helvetica", "bold");
  doc.text(data.customerName, 16, y + 14);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(`Reason: ${CREDIT_REASONS.find(r => r.value === data.reason)?.label || data.reason}`, 16, y + 22);

  // Items table header
  y = 110;
  doc.setFont("helvetica", "bold");
  doc.rect(14, y, 5,   8); doc.text("#",     15,   y + 5.5);
  doc.rect(19, y, 70,  8); doc.text("Description", 20, y + 5.5);
  doc.rect(89, y, 15,  8); doc.text("HSN",  90,   y + 5.5);
  doc.rect(104, y, 14, 8); doc.text("Qty",  105,  y + 5.5);
  doc.rect(118, y, 20, 8); doc.text("Rate", 119,  y + 5.5);
  doc.rect(138, y, 15, 8); doc.text("Tax%", 139,  y + 5.5);
  doc.rect(153, y, 43, 8); doc.text("Amount",154, y + 5.5);

  // Items rows
  doc.setFont("helvetica", "normal");
  data.items.forEach((item: INoteItem, i: number) => {
    y += 8;
    doc.rect(14, y, 5,   8); doc.text(`${i+1}`,              15,   y + 5.5);
    doc.rect(19, y, 70,  8); doc.text(item.description || "",20,   y + 5.5);
    doc.rect(89, y, 15,  8); doc.text(item.hsn || "",        90,   y + 5.5);
    doc.rect(104, y, 14, 8); doc.text(`${item.quantity}`,    105,  y + 5.5);
    doc.rect(118, y, 20, 8); doc.text(`${item.rate}`,        119,  y + 5.5);
    doc.rect(138, y, 15, 8); doc.text(`${item.tax}%`,        139,  y + 5.5);
    doc.rect(153, y, 43, 8); doc.text(item.amount.toFixed(2),154,  y + 5.5);
  });

  // Totals
  y += 14;
  doc.setFont("helvetica", "bold");
  doc.text(`Subtotal: ${fmt(data.subTotal)}`, W - 80, y);
  doc.text(`Tax: ${fmt(data.totalTax)}`,      W - 80, y + 7);
  doc.text(`Grand Total (Credit): ${fmt(data.grandTotal)}`, W - 80, y + 14);

  // Notes
  if (data.notes) {
    y += 28;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(`Notes: ${data.notes}`, 14, y);
  }

  // Footer
  doc.setFontSize(8); doc.setTextColor(150);
  doc.text("This is a Computer Generated Credit Note", W / 2, H - 5, { align: "center" });
  doc.setTextColor(0);

  doc.save(`CreditNote_${cnId}.pdf`);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Customer { Id: string; customerName?: string; companyName?: string; phone?: string; }
interface Invoice  { invoiceId: string; grandTotal: number; dueAmount: number; status: string; }

const CreateCreditNote: React.FC = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));

  const orgId     = localStorage.getItem("orgId")     ?? "";
  const createdBy = localStorage.getItem("userId")    ?? orgId;
  //const orgDetails= JSON.parse(localStorage.getItem("orgDetails") || "{}");
  const [orgDetails, setOrgDetails] = useState<any>(null);

  // ── State ─────────────────────────────────────────────────────────────
  const [customers,        setCustomers]        = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [invoices,         setInvoices]         = useState<Invoice[]>([]);
  const [selectedInvoice,  setSelectedInvoice]  = useState<Invoice | null>(null);
  const [reason,           setReason]           = useState<CreditNoteReason>("RETURN");
  const [cnDate,           setCnDate]           = useState(new Date().toISOString().split("T")[0]);
  const [notes,            setNotes]            = useState("");
  const [items,            setItems]            = useState<INoteItem[]>([emptyItem()]);
  const [submitting,       setSubmitting]       = useState(false);
  const [error,            setError]            = useState("");
  const [successCnId,      setSuccessCnId]      = useState("");
   //Org Load
  useEffect(() => {
  if (!orgId) return;
  getOrganizationDetails(orgId)
    .then((data) => {
      setOrgDetails(data);
    })
    .catch((err) => {
      console.error("Failed to load org details", err);
    });
}, [orgId]);
  // ── Load customers ────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    getUsers(orgId, "customer")
      .then((data) => setCustomers(data ?? []))
      .catch(console.error);
  }, [orgId]);

  // ── Load invoices when customer selected ──────────────────────────────
  useEffect(() => {
    if (!selectedCustomer || !orgId) { setInvoices([]); setSelectedInvoice(null); return; }
    getOutstanding(orgId, selectedCustomer.Id)
      .then((data) => setInvoices(data as any ?? []))
      .catch(() => setInvoices([]));
  }, [selectedCustomer, orgId]);

  // ── Item helpers ──────────────────────────────────────────────────────
  const updateItem = (idx: number, field: keyof INoteItem, value: any) => {
    setItems((prev) => {
      const next = prev.map((item, i) => {
        if (i !== idx) return item;
        const updated = { ...item, [field]: value };
        updated.amount = computeAmount(updated);
        return updated;
      });
      return next;
    });
  };

  const addItem    = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (idx: number) => setItems((p) => p.filter((_, i) => i !== idx));

  // ── Totals ────────────────────────────────────────────────────────────
  const subTotal     = items.reduce((s, i) => s + i.quantity * i.rate * (1 - i.discount / 100), 0);
  const totalTax     = items.reduce((s, i) => s + i.quantity * i.rate * (1 - i.discount / 100) * (i.tax / 100), 0);
  const totalDisc    = items.reduce((s, i) => s + i.quantity * i.rate * (i.discount / 100), 0);
  const grandTotal   = subTotal + totalTax;

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedCustomer || !selectedInvoice || grandTotal <= 0) {
      setError("Please select customer, invoice and add at least one item.");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const payload = {
        orgId,
        customerId:    selectedCustomer.Id,
        customerName:  selectedCustomer.customerName ?? selectedCustomer.companyName ?? "",
        referenceId:   selectedInvoice.invoiceId,
        reason,
        cnDate,
        items:         items.map((i) => ({ ...i, amount: computeAmount(i) })),
        subTotal:      parseFloat(subTotal.toFixed(2)),
        totalTax:      parseFloat(totalTax.toFixed(2)),
        totalDiscount: parseFloat(totalDisc.toFixed(2)),
        grandTotal:    parseFloat(grandTotal.toFixed(2)),
        notes,
        createdBy,
      };
      const res = await createCreditNote(payload);
      setSuccessCnId(res.cnId);
      printCreditNotePDF(res.cnId, payload, orgDetails);
    } catch (err: any) {
      setError(err?.message || "Failed to create credit note.");
    } finally {
      setSubmitting(false);
    }
  };

  const custLabel = (c: Customer) =>
    `${c.customerName ?? c.companyName ?? ""}${c.phone ? " | " + c.phone : ""}`;

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>Back</Button>
        <Typography variant="caption" color="text.secondary">Receipt › Credit Note</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <NoteAltIcon color="success" />
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color="success.main">
          Create Credit Note
        </Typography>
        <Chip label="CR" color="success" size="small" />
      </Stack>

      {/* Success */}
      {successCnId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Credit Note <strong>{successCnId}</strong> created and PDF downloaded.{" "}
          <Button size="small" onClick={() => { setSuccessCnId(""); setItems([emptyItem()]); setSelectedCustomer(null); }}>
            Create Another
          </Button>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* ── Left column ── */}
        <Grid item xs={12} md={8}>

          {/* Customer + Invoice */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                CUSTOMER & INVOICE DETAILS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={custLabel}
                    value={selectedCustomer}
                    onChange={(_, v) => { setSelectedCustomer(v); setSelectedInvoice(null); }}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    renderInput={(params) => <TextField {...params} label="Customer *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={invoices}
                    getOptionLabel={(inv: any) =>
                      `${inv.invoiceId} — Due: ₹${Number(inv.dueAmount).toLocaleString("en-IN")}`
                    }
                    value={selectedInvoice}
                    onChange={(_, v) => setSelectedInvoice(v)}
                    disabled={!selectedCustomer}
                    isOptionEqualToValue={(o: any, v: any) => o.invoiceId === v.invoiceId}
                    renderInput={(params) => <TextField {...params} label="Against Invoice *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason *</InputLabel>
                    <Select value={reason} label="Reason *" onChange={(e) => setReason(e.target.value as CreditNoteReason)}>
                      {CREDIT_REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Credit Note Date *" type="date" size="small" fullWidth
                    value={cnDate} onChange={(e) => setCnDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  ITEMS / SERVICES
                </Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" color="success" onClick={addItem}>
                  Add Row
                </Button>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "grey.50" }}>
                    <TableRow>
                      {["#","Description","HSN","Qty","Rate (₹)","Disc %","Tax %","Amount (₹)",""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11, whiteSpace: "nowrap" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField
                            size="small" variant="standard" placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateItem(idx, "description", e.target.value)}
                            sx={{ minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="HSN" value={item.hsn}
                            onChange={(e) => updateItem(idx, "hsn", e.target.value)} sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.quantity}
                            onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 1 }} sx={{ width: 55 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.rate}
                            onChange={(e) => updateItem(idx, "rate", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0 }} sx={{ width: 75 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.discount}
                            onChange={(e) => updateItem(idx, "discount", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, max: 100 }} sx={{ width: 55 }} />
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" value={item.tax}
                            onChange={(e) => updateItem(idx, "tax", Number(e.target.value))}
                            sx={{ width: 65 }}>
                            {TAX_RATES.map((r) => <MenuItem key={r} value={r}>{r}%</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "success.main" }}>
                          {computeAmount(item).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" color="error" onClick={() => removeItem(idx)}
                              disabled={items.length === 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>NOTES</Typography>
              <TextField
                multiline rows={3} fullWidth size="small"
                placeholder="Internal notes or reason details..."
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right column — Summary ── */}
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, position: { md: "sticky" }, top: { md: 80 } }}
          >
            <Box sx={{ bgcolor: "success.main", px: 2, py: 1.5, borderRadius: "8px 8px 0 0" }}>
              <Typography color="white" fontWeight={700}>Credit Note Summary</Typography>
            </Box>
            <CardContent>
              {selectedInvoice && (
                <>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Invoice Ref</Typography>
                    <Typography variant="body2" fontWeight={500}>{(selectedInvoice as any).invoiceId}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">Invoice Due</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      {fmt((selectedInvoice as any).dueAmount)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 1.5 }} />
                </>
              )}

              {[
                { label: "Subtotal",  value: subTotal },
                { label: "Discount",  value: totalDisc },
                { label: "Tax",       value: totalTax },
              ].map(({ label, value }) => (
                <Stack key={label} direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{fmt(value)}</Typography>
                </Stack>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography fontWeight={700}>Credit Amount</Typography>
                <Typography fontWeight={700} color="success.main" variant="h6">
                  {fmt(grandTotal)}
                </Typography>
              </Stack>

              {grandTotal > 0 && selectedInvoice && (
                <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                  After this credit note, invoice due will be{" "}
                  <strong>{fmt(Math.max((selectedInvoice as any).dueAmount - grandTotal, 0))}</strong>
                </Alert>
              )}

              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={!selectedCustomer || !selectedInvoice || grandTotal <= 0 || submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Saving..." : "Create Credit Note"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreateCreditNote;